import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import get from 'lodash/get';
import {
  IonPopover,
  IonButton,
  IonList,
  IonItem,
  IonFabButton,
  IonLabel,
  IonNote,
  IonIcon,
} from '@ionic/react';
import { formatCurrency } from 'utils/currency';
import { addOutline, removeOutline, checkmarkOutline } from 'ionicons/icons';
import apiCreate from 'services/api';
import PayActions, { PaySelectors } from 'redux/PayRedux';
import { AuthSelectors } from 'redux/AuthRedux';
import AppActions, { AppSelectors } from 'redux/AppRedux';

function MenuItem({ canPay, item }) {
  const dispatch = useDispatch();
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(item.selectedQuantity);
  const token = useSelector(AuthSelectors.selectToken);
  const venueId = useSelector(AppSelectors.selectVenue);
  const user = useSelector(AuthSelectors.selectUser);
  const ticket = useSelector(PaySelectors.selectTicket);
  let icon = null;

  const allSold = item.paidQuantity === item.quantity;
  if (canPay) {
    if (
      item.paidQuantity + item.claimedQuantity + item.selectedQuantity ===
      0
    ) {
      icon = (
        <IonIcon
          className="menu-item-icon"
          icon={addOutline}
          color="tertiary"
        />
      );
    } else if (
      item.paidQuantity + item.claimedQuantity + item.selectedQuantity ===
      item.quantity
    ) {
      icon = (
        <IonIcon
          className="menu-item-icon"
          icon={checkmarkOutline}
          color="success"
        />
      );
    } else {
      icon = (
        <IonIcon className="menu-item-icon" icon={addOutline} color="success" />
      );
    }
  }

  const handleClick = async () => {
    dispatch(PayActions.setLoadingTicket(true));
    const api = apiCreate({ venueId, token });
    const resp = await api.getClaims(ticket.id);
    if (resp.ok) {
      dispatch(PayActions.setTicket({ ...ticket, ...resp.data }));
      dispatch(
        PayActions.setSelectedItems(get(resp.data, `claims.${user.id}`) || [])
      );
    }
    dispatch(PayActions.setLoadingTicket(false));

    if (canPay && !allSold) {
      if (item.remainingQuantity === 0) {
        dispatch(AppActions.setToast('This item is already claimed', 'danger'));
      } else if (item.remainingQuantity === 1) {
        dispatch(
          PayActions.updateSelectedItem(
            item,
            item.selectedQuantity === 0 ? 1 : 0
          )
        );
      } else {
        setOpen(true);
      }
    }
  };

  const handleAdd = () => {
    setSelected(Math.min(selected + 1, item.remainingQuantity));
  };

  const handleRemove = () => {
    setSelected(Math.max(0, selected - 1));
  };

  const updateQuantity = () => {
    ref.current.dismiss();
    dispatch(PayActions.updateSelectedItem(item, selected));
  };

  useEffect(() => {
    if (open && item.remainingQuantity === 0) {
      setOpen(false);
      dispatch(AppActions.setToast('This item is already claimed', 'danger'));
    }
    setSelected(item.selectedQuantity);
  }, [open]);

  return (
    <IonItem
      className={item.selectedQuantity > 0 ? 'menuitem-selected' : ''}
      lines="full"
      button={canPay && !allSold}
      onClick={canPay ? handleClick : () => {}}
    >
      {icon}
      <IonLabel color={allSold ? 'medium' : 'primary'}>
        {Math.round(item.quantity)}&nbsp;&nbsp;
        {item.name}
      </IonLabel>
      <IonNote
        slot="end"
        color={allSold ? 'medium' : 'primary'}
        className="menuitem-price"
      >
        {formatCurrency(item.preDiscountPrice)}
      </IonNote>
      <IonPopover ref={ref} isOpen={open} onDidDismiss={() => setOpen(false)}>
        <IonList onClick={(e) => e.stopPropagation()}>
          <IonItem lines="none">
            <IonLabel className="text-center">{item.name}</IonLabel>
          </IonItem>
          <IonItem lines="none" className="menu-popover-item">
            <IonFabButton
              size="small"
              onClick={handleRemove}
              disabled={selected === 0}
            >
              <IonIcon icon={removeOutline} />
            </IonFabButton>
            <IonLabel className="text-center text-bold">{selected}</IonLabel>
            <IonFabButton
              size="small"
              onClick={handleAdd}
              disabled={selected === item.remainingQuantity}
            >
              <IonIcon icon={addOutline} />
            </IonFabButton>
          </IonItem>
          <div className="ion-padding">
            <IonButton
              color="tertiary"
              expand="block"
              fill="solid"
              type="button"
              onClick={updateQuantity}
            >
              Add
            </IonButton>
          </div>
        </IonList>
      </IonPopover>
    </IonItem>
  );
}

MenuItem.propTypes = {
  item: PropTypes.object,
  canPay: PropTypes.bool,
};

export default MenuItem;
